import { VenueRequestFile } from '@go-shared/types/groupBooking';
import React from 'react';

import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleSelect, BubbleFileM } from '../BubbleUI';
import { FormGenericContainer } from '../FormGenericContainer';
import { GroupForm } from './Workflows';
import { BrandGroupType } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import Editor from '../TinyEditor';
import { getCommonTexts } from '@go-shared/lib/brand';
import TinyEditor from '../TinyEditor';
import BubbleCollapsibleSection from '../BubbleUI/BubbleCollapsibleSection';
import { RadioButton, RadioButtonGroup } from '@spark-labs/avocado-ui';

interface EnterGroupPageProps {
  groupForm: GroupForm;
  changeGroupForm: <T extends keyof GroupForm>(key: T, val: GroupForm[T]) => void;
  groupTypeOpts: BrandGroupType[];
  title?: string;
  pageIndex?: number;
  onClose?: () => void;
  onSubmitGroupDetails: () => void;
  onUpdateFile: (file: VenueRequestFile) => void;
  onUploadFile: (file: File) => void;
  onDeleteFile: (file: VenueRequestFile) => void;
}

export default function EnterGroupPage(props: EnterGroupPageProps) {
  const formValid = props.groupForm.name.length > 0;
  const canSubmit = formValid;

  return (
    <div className="tflf-1">
      <FormGenericContainer
        className="h-screen flex flex-col"
        bodyClassName="flex-grow flex flex-col"
        footer={() => (
          <>
            <div className="text-slate-dark"></div>
            <BubbleButton
              className="ml-auto"
              variant="navy"
              disabled={!canSubmit}
              onClick={props.onSubmitGroupDetails}
            >
              Finish: Submit Group Details
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div className="mr-auto">
            <h1 className="text-lg text-slate-dark font-semibold">{props.title || 'Edit Group'}</h1>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Enter Group Details
            </h2>
          </div>
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onClose}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <EnterGroupPageFields
          partyForm={props.groupForm}
          changePartyForm={props.changeGroupForm}
          groupTypeOpts={props.groupTypeOpts}
          onUpdateFile={props.onUpdateFile}
          onUploadFile={props.onUploadFile}
          onDeleteFile={props.onDeleteFile}
        />
      </FormGenericContainer>
    </div>
  );
}

interface EnterGroupPageFieldProps {
  partyForm: GroupForm;
  changePartyForm: <T extends keyof GroupForm>(key: T, val: GroupForm[T]) => void;
  groupTypeOpts: BrandGroupType[];
  className?: string;
  onUpdateFile?: (file: VenueRequestFile) => void;
  onUploadFile?: (file: File) => void;
  onDeleteFile?: (file: VenueRequestFile) => void;
}

export function EnterGroupPageFields(props: EnterGroupPageFieldProps) {
  const { brand } = useServerInfo();

  const {
    guest_experience,
    tour_leader,
    tour_leader_cabin,
    guests,
    group_contact_name,
    embark_date,
    debark_date,
  } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  return (
    <div>
      <div className={`grid grid-cols-3 gap-4 items-center ${props.className}`}>
        <BubbleFormGroup label="Group Name" asterisk>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.name}
            onValue={val => props.changePartyForm('name', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Group ID">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.groupId}
            onValue={val => props.changePartyForm('groupId', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Group Type">
          <BubbleSelect
            block
            allowUndefined
            options={brand.groupTypes}
            labelExtractor={gt => gt.name}
            value={props.partyForm.groupType}
            onValue={val => props.changePartyForm('groupType', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={group_contact_name} className="col-span-2">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.contact}
            onValue={val => props.changePartyForm('contact', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={`${guests} (min. 0)`}>
          <BubbleInput
            type="number"
            block
            min="0"
            value={props.partyForm.size}
            onValue={val => props.changePartyForm('size', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={tour_leader}>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.groupGuideName}
            onValue={val => props.changePartyForm('groupGuideName', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={`${tour_leader_cabin}`}>
          <BubbleInput
            type="text"
            block
            value={props.partyForm.groupGuideCabin}
            onValue={val => props.changePartyForm('groupGuideCabin', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Booking No.">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.bookingNo}
            onValue={val => props.changePartyForm('bookingNo', val)}
          />
        </BubbleFormGroup>
        {/* salesOffice renamed to Agengy */}
        <BubbleFormGroup label="Agency">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.salesOffice}
            onValue={val => props.changePartyForm('salesOffice', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Sales Dept Email">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.salesEmail}
            onValue={val => props.changePartyForm('salesEmail', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Sales Dept phone">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.salesPhone}
            onValue={val => props.changePartyForm('salesPhone', val)}
          />
        </BubbleFormGroup>
        <div className="col-span-3 grid grid-cols-2 gap-4 items-center">
          <BubbleFormGroup label={embark_date}>
            <BubbleInput
              type="date"
              block
              value={props.partyForm.embark}
              onValue={val => props.changePartyForm('embark', val)}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label={debark_date}>
            <BubbleInput
              type="date"
              block
              value={props.partyForm.debark}
              onValue={val => props.changePartyForm('debark', val)}
            />
          </BubbleFormGroup>
        </div>
        <BubbleFormGroup label="Cabin Drop Requests" className="col-span-3">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.cabinDrops}
            onValue={val => props.changePartyForm('cabinDrops', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Dining Preference" className="col-span-3">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.diningPreference}
            onValue={val => props.changePartyForm('diningPreference', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Pre-Paid Packages" className="col-span-3">
          <BubbleInput
            type="text"
            block
            value={props.partyForm.prepaidPackages}
            onValue={val => props.changePartyForm('prepaidPackages', val)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label={guest_experience} className="col-span-3">
          <TinyEditor
            value={props.partyForm.guestExperience}
            onValue={val => props.changePartyForm('guestExperience', val)}
            mode="text"
          />
        </BubbleFormGroup>

        <BubbleFormGroup label="Group Notes" className="col-span-3">
          <Editor
            value={props.partyForm.notes}
            onValue={val => props.changePartyForm('notes', val)}
            mode="text"
            config={init => ({ ...init })}
          />
        </BubbleFormGroup>
        <div className="col-span-3">
          <BubbleFileM
            label="Group Files"
            files={props.partyForm.files}
            onUpdateFile={props.onUpdateFile}
            onUploadFile={props.onUploadFile}
            onDeleteFile={props.onDeleteFile}
          />
        </div>
        <BubbleFormGroup className="flex items-center col-span-3">
          <BubbleInput
            type="checkbox"
            block
            checked={props.partyForm.hospitalityDesk}
            onValue={val => props.changePartyForm('hospitalityDesk', val)}
            className="mr-2"
            inputClassName="bg-white"
          />
          <span>Hospitality Desk Required</span>
        </BubbleFormGroup>

        <BubbleCollapsibleSection
          title="Balance & Payment Status"
          variant="transparent"
          defaultOpen={false}
          className="col-span-3"
        >
          <div className="grid grid-cols-3 gap-4 items-start">
            <BubbleFormGroup label="Balance Due" className="col-span-1">
              <BubbleInput
                block
                type="text"
                value={props.partyForm.balanceDue}
                onValue={val => props.changePartyForm('balanceDue', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Payment Status" className="col-span-2">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                  <RadioButtonGroup
                    key={`payment-status-${props.partyForm.paymentStatus || 'empty'}`}
                    name="paymentStatus"
                    onChange={val => props.changePartyForm('paymentStatus', val)}
                    groupValue={props.partyForm.paymentStatus || ''}
                  >
                    {paymentStatusProps => (
                      <>
                        <div className="flex items-center">
                          <RadioButton label="Pending" value={'pending'} {...paymentStatusProps} />
                        </div>
                        <div className="flex items-center">
                          <RadioButton label="Full" value={'full'} {...paymentStatusProps} />
                        </div>
                        <div className="flex items-center">
                          <RadioButton
                            label="Partially Collected"
                            value={'partially-collected'}
                            {...paymentStatusProps}
                          />
                        </div>
                      </>
                    )}
                  </RadioButtonGroup>

                  <BubbleButton
                    variant="smoke"
                    size="small"
                    onClick={() => props.changePartyForm('paymentStatus', '')}
                    className="mt-2 self-start"
                  >
                    Clear Selection
                  </BubbleButton>
                </div>
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleCollapsibleSection>
      </div>
    </div>
  );
}
